import React, { useState } from 'react';
import { shareRecipe } from 'wasp/client/operations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Alert, AlertDescription } from '../ui/alert';
import { Copy, Check, Globe, Link as LinkIcon, Lock, Eye, Users } from 'lucide-react';
import { toast } from 'sonner';

interface Recipe {
  id: string;
  title: string;
  visibility?: string;
  shareSlug?: string | null;
  viewCount?: number;
  saveCount?: number;
}

interface ShareRecipeDialogProps {
  recipe: Recipe;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ShareRecipeDialog({ recipe, isOpen, onClose, onSuccess }: ShareRecipeDialogProps) {
  const [visibility, setVisibility] = useState<'private' | 'unlisted' | 'public'>(
    (recipe.visibility as any) || 'private'
  );
  const [customizationNote, setCustomizationNote] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleShare = async () => {
    setIsLoading(true);
    try {
      const result = await shareRecipe({
        recipeId: recipe.id,
        visibility,
        customizationNote: customizationNote || undefined,
      });

      if (result.success && result.shareUrl) {
        setShareUrl(result.shareUrl);
        toast.success(result.message || 'Recipe sharing settings updated!');
        onSuccess?.();
      } else if (result.success && visibility === 'private') {
        toast.success('Recipe is now private');
        setShareUrl(null);
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error sharing recipe:', error);
      toast.error('Failed to update sharing settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareUrl(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Share Your Recipe</DialogTitle>
          <DialogDescription>
            Share "{recipe.title}" with others
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Privacy Settings */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Who can see this recipe?</Label>
            <RadioGroup value={visibility} onValueChange={(v: any) => setVisibility(v)}>
              <div className="flex items-start space-x-3 rounded-lg border border-stone/20 p-3 transition-colors hover:bg-cream/20">
                <RadioGroupItem value="private" id="private" className="mt-1" />
                <Label htmlFor="private" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 font-medium text-dusk">
                    <Lock className="h-4 w-4" />
                    Private
                  </div>
                  <div className="mt-1 text-sm text-supporting">
                    Only you can see this recipe
                  </div>
                </Label>
              </div>

              <div className="flex items-start space-x-3 rounded-lg border border-stone/20 p-3 transition-colors hover:bg-cream/20">
                <RadioGroupItem value="unlisted" id="unlisted" className="mt-1" />
                <Label htmlFor="unlisted" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 font-medium text-dusk">
                    <LinkIcon className="h-4 w-4" />
                    Unlisted
                  </div>
                  <div className="mt-1 text-sm text-supporting">
                    Anyone with the link can view (not searchable)
                  </div>
                </Label>
              </div>

              <div className="flex items-start space-x-3 rounded-lg border border-stone/20 p-3 transition-colors hover:bg-cream/20">
                <RadioGroupItem value="public" id="public" className="mt-1" />
                <Label htmlFor="public" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 font-medium text-dusk">
                    <Globe className="h-4 w-4" />
                    Public
                  </div>
                  <div className="mt-1 text-sm text-supporting">
                    Visible in community recipe gallery
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Customization Note */}
          {visibility !== 'private' && (
            <div className="space-y-2">
              <Label htmlFor="note">What did you customize? (optional)</Label>
              <Textarea
                id="note"
                placeholder="e.g., I reduced sugar by half and added cinnamon!"
                value={customizationNote}
                onChange={(e) => setCustomizationNote(e.target.value)}
                maxLength={200}
                className="resize-none"
                rows={3}
              />
              <div className="text-xs text-supporting text-right">
                {customizationNote.length}/200
              </div>
            </div>
          )}

          {/* Share URL */}
          {shareUrl && (
            <div className="space-y-2">
              <Label>Share Link</Label>
              <div className="flex gap-2">
                <Input 
                  value={shareUrl} 
                  readOnly 
                  className="flex-1 bg-cream/30 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyUrl}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {/* Analytics */}
              {(recipe.viewCount !== undefined || recipe.saveCount !== undefined) && (
                <div className="flex gap-4 pt-2 text-sm text-supporting">
                  {recipe.viewCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {recipe.viewCount} views
                    </span>
                  )}
                  {recipe.saveCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {recipe.saveCount} saves
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info Alert */}
          {visibility !== 'private' && !shareUrl && (
            <Alert>
              <AlertDescription className="text-sm">
                A unique share link will be generated when you save these settings.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleShare} disabled={isLoading}>
            {isLoading ? 'Saving...' : shareUrl ? 'Update Settings' : 'Create Share Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
